import { useParams, useLoaderData, useNavigate } from 'react-router-dom'
import { usePreview } from '@/hooks'
import { ROUTES } from '@/constants/routes'
import { useAppointmentPageRefresh } from '@/hooks/useAppointmentPageRefresh'
import type { PreJoinLoaderData } from '@/app/loaders/prejoinLoader'
import { ConsentCard } from '@/components/molecules/ConsentCard'
import { AppTemplate } from '@/components/templates/AppTemplate/AppTemplate'
import { MeetingHeader } from '@/components/molecules/Header/MeetingHeader'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/Avatar/Avatar'
import { Card, CardContent } from '@/components/ui/Card/Card'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger
} from '@/components/ui/Drawer/Drawer'
import { Button } from '@/components/ui/Button/Button'
import { DeviceSelector } from '@/components/molecules/DeviceSelector'
import { PhoneIcon } from 'lucide-react'
import { TimeStatus } from '@/models'
import { marketplaceConversationUrl } from '@/utils/conversation.utils'

export const PhoneWaitingRoom: React.FC = () => {
  const { id: appointmentId } = useParams<{ id: string }>()
  const normalizedAppointmentId = appointmentId?.trim() ?? ''

  // Load appointment data via loader
  const { appointment: appointmentData } = useLoaderData() as PreJoinLoaderData

  const navigate = useNavigate()

  // Preview and device management (browser-native, no Chime session needed)
  const { preview } = usePreview({
    appointmentId: normalizedAppointmentId
  })

  // Program automatic page refresh at appointment start + 10s
  useAppointmentPageRefresh({
    appointmentFrom: appointmentData.from,
    currentTimeStatus: appointmentData.timeStatus
  })

  // Use backend status directly (no more local override)
  const timeStatus = appointmentData.timeStatus

  const handleJoinMeeting = () => {
    if (!normalizedAppointmentId) return
    navigate(ROUTES.joinPhoneMeeting(normalizedAppointmentId))
  }

  const participant = appointmentData.interlocutor

  const formatDate = (isoDate: string) => {
    const date = new Date(isoDate)
    const options: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }
    const formattedDate = new Intl.DateTimeFormat('fr-FR', options).format(date)
    const time = date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    })
    return `${formattedDate} à ${time}`
  }

  if (!participant) {
    return (
      <AppTemplate
        header={
          <>
            <div className="bg-primary lg:hidden">
              <MeetingHeader centered />
            </div>
            <div className="hidden lg:block">
              <MeetingHeader showVideocallTag />
            </div>
          </>
        }
      >
        <div className="flex items-center justify-center p-8">
          <p className="text-secondary">Aucun participant trouvé pour ce rendez-vous.</p>
        </div>
      </AppTemplate>
    )
  }

  const conversationUrl = marketplaceConversationUrl(appointmentData.conversationId)

  return (
    <AppTemplate
      header={
        <>
          <div className="bg-primary lg:hidden">
            <MeetingHeader centered />
          </div>
          <div className="hidden lg:block">
            <MeetingHeader />
          </div>
        </>
      }
    >
      <div className="flex w-full max-w-7xl flex-col gap-6 h-[calc(100dvh-48px)] lg:h-auto">
        <Card className="shadow-none flex-1 lg:flex-none rounded-none lg:rounded-xl border-none lg:border bg-primary text-white">
          <CardContent className="flex flex-col h-full p-0">
            <div className="flex flex-col justify-between h-[calc(100dvh-48px)] lg:h-auto py-12 lg:py-10 w-full px-4 lg:px-10">
              <div className="flex flex-col items-center px-4 gap-y-2">
                <Avatar className="h-24 w-24 mb-2">
                  {participant.avatar ? (
                    <AvatarImage src={participant.avatar} />
                  ) : (
                    <AvatarFallback>{participant.firstName.charAt(0)}</AvatarFallback>
                  )}
                </Avatar>

                <p className="text-xl font-medium">Rendez-vous My Job Glasses</p>
                <p className="text-xl text-gray-300">
                  Avec {participant.firstName} {participant.lastName}
                </p>

                <p className="text-gray-300 mt-12">{formatDate(appointmentData.from)}</p>
              </div>

              <div className="flex flex-col gap-y-6 w-full">
                {timeStatus === TimeStatus.Current && (
                  <div className="flex items-center justify-between lg:justify-center px-10 mb-8 lg:mt-12 lg:gap-x-24">
                    <DeviceSelector
                      selectedAudioInput={preview.selectedAudioInput}
                      selectedAudioOutput={preview.selectedAudioOutput}
                      audioInputs={preview.audioInputs.map((device) => ({
                        id: device.id,
                        label: device.label
                      }))}
                      audioOutputs={preview.audioOutputs.map((device) => ({
                        id: device.id,
                        label: device.label
                      }))}
                      onAudioInputChange={(id) => void preview.selectAudioInput({ id })}
                      onAudioOutputChange={(id) => void preview.selectAudioOutput({ id })}
                      buttonSize="lg"
                      label="Réglages"
                    />

                    <div className="flex flex-col items-center gap-y-2">
                      <Button
                        size="onlyIcon"
                        className="rounded-full bg-green-500 hover:bg-green-200 w-16 h-16"
                        onClick={handleJoinMeeting}
                      >
                        <PhoneIcon />
                      </Button>
                      <span className="text-xs">Appeler</span>
                    </div>
                  </div>
                )}
                {timeStatus === TimeStatus.Future && (
                  <p className="mb-4 text-center text-gray-300 text-xl">
                    Vous pourrez rejoindre l'appel 10mn avant le rdv
                  </p>
                )}

                <Drawer>
                  <DrawerTrigger asChild className="lg:hidden mb-4">
                    <Button className="border">En savoir plus sur la visio intégrée</Button>
                  </DrawerTrigger>
                  <DrawerContent className="bg-white max-h-[95dvh]">
                    <DrawerHeader>
                      <DrawerTitle className="text-start">En savoir plus sur la visio intégrée</DrawerTitle>
                    </DrawerHeader>
                    <div className="no-scrollbar overflow-y-auto px-4">
                      <ConsentCard phoneNumber={participant.phone} />
                    </div>
                    <DrawerFooter>
                      <DrawerClose asChild>
                        <Button className="w-full" variant="secondary">
                          Fermer
                        </Button>
                      </DrawerClose>
                    </DrawerFooter>
                  </DrawerContent>
                </Drawer>

                <Button asChild variant="link" className="hidden lg:flex text-white">
                  <a href={conversationUrl}>Retour sur la conversation</a>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <ConsentCard className="hidden lg:flex" phoneNumber={participant.phone} />
      </div>
    </AppTemplate>
  )
}